// =================================================================
// useCurrentContext hook
//
// The adapter that unifies the three identity sources into a single
// surface the rest of the app — and especially the games catalog —
// can read without knowing which path the user took to get here.
//
// Three identity sources feed in:
//   - useResident       → community resident (login-code session)
//   - useIndividualUser → individual subscriber (Firebase Auth + /users/)
//   - useDevice         → device-bundled tablet (deviceId session)
//
// This hook returns:
//   {
//     loading:        bool — true while any provider is hydrating
//     type:           'community_resident' | 'individual_user' | 'device' | 'none'
//     id:             stable identifier for this user/device (or null)
//     displayName:    what to greet the user with (or null)
//     entitlementOk:  is this context allowed to play paid content?
//     rtdbPath:       where their data lives under /brain_health/
//                     (e.g. 'brain_health/accounts/abc123'). The Dashboard
//                     and session-write code consume this as `basePath`.
//     record:         the underlying user/device record. Identity-source-
//                     agnostic — Dashboard reads this for display_name,
//                     assessment, goals, sessions, etc. Schema is the
//                     same regardless of which root it came from
//                     (snake_case, see database.rules.json).
//     raw:            the underlying provider record wrapped in a
//                     {residentAccount|device|userRecord} shape, in
//                     case the consumer needs identity-source-specific
//                     data. Rare; prefer `record` above.
//   }
//
// IMPORTANT: the database uses snake_case throughout (`display_name`,
// not `displayName`) — see database.rules.json. This hook reads
// snake_case from the underlying records and exposes camelCase on
// the unified surface, because JS consumers prefer camelCase. The
// translation happens here, in one place.
//
// Game code should treat the return value as the source of truth.
// Adding a new identity source later (org-bundled? family-of-three
// shared sub?) is an internal change here; game code stays unchanged.
// =================================================================

import { createContext, useContext, useMemo } from 'react';
import { useResident } from './useResident.jsx';
import { useDevice } from './useDevice.jsx';
import { useIndividualUser } from './useIndividualUser.jsx';

export const CONTEXT_TYPES = Object.freeze({
  COMMUNITY_RESIDENT: 'community_resident',
  INDIVIDUAL_USER: 'individual_user',
  DEVICE: 'device',
  NONE: 'none',
});

// Exported so alternative providers (e.g. the preview walkthrough)
// can supply a mock value through the same context the hook reads.
// PREVIEW NOTE: the export itself is harmless to keep, but it was
// added to support /assessment/preview.
export const CurrentContextContext = createContext(null);

// Priority order, top wins. The reasoning, in order:
//
//   1. DEVICE first. A device session means we're running on a
//      Hexnode-provisioned kiosked tablet. That physical context is
//      the most specific signal we have — if there's ALSO a resident
//      or auth session present in localStorage for some reason
//      (developer testing, browser shared with admin laptop, etc.),
//      the device context still wins because that's what the tablet
//      is for.
//
//   2. COMMUNITY_RESIDENT next. A resident login-code session is an
//      explicit sign-in act with a clear scope (this community).
//      Should override an auth session because the auth session
//      might just be a logged-in admin browsing.
//
//   3. INDIVIDUAL_USER last. Firebase Auth users are the most
//      generic identity — they might be admins, individual subs,
//      or both. Only treat as "individual" if no more specific
//      context exists AND they have a /users/ record.
const PRIORITY = [
  CONTEXT_TYPES.DEVICE,
  CONTEXT_TYPES.COMMUNITY_RESIDENT,
  CONTEXT_TYPES.INDIVIDUAL_USER,
];

export function CurrentContextProvider({ children }) {
  const {
    account: residentAccount,
    loading: residentLoading,
  } = useResident();

  const {
    device,
    loading: deviceLoading,
    hasEntitlement: deviceHasEntitlement,
  } = useDevice();

  const {
    userRecord,
    loading: individualLoading,
    hasEntitlement: individualHasEntitlement,
  } = useIndividualUser();

  const value = useMemo(() => {
    const loading =
      residentLoading || deviceLoading || individualLoading;

    if (loading) {
      return {
        loading: true,
        type: null,
        id: null,
        displayName: null,
        entitlementOk: false,
        rtdbPath: null,
        record: null,
        raw: null,
      };
    }

    // Walk priority order. First hit wins.
    for (const type of PRIORITY) {
      if (type === CONTEXT_TYPES.DEVICE && device) {
        return {
          loading: false,
          type: CONTEXT_TYPES.DEVICE,
          id: device.id,
          // Snake_case in the DB → camelCase on the unified surface.
          displayName: device.display_name || null,
          entitlementOk: deviceHasEntitlement,
          rtdbPath: `brain_health/devices/${device.id}`,
          record: device,
          raw: { device },
        };
      }
      if (
        type === CONTEXT_TYPES.COMMUNITY_RESIDENT &&
        residentAccount
      ) {
        return {
          loading: false,
          type: CONTEXT_TYPES.COMMUNITY_RESIDENT,
          id: residentAccount.id,
          // useResident returns the raw account record with snake_case
          // fields — display_name, NOT displayName.
          displayName: residentAccount.display_name || null,
          // Community residents are entitled implicitly — the
          // community's subscription is the admin's responsibility,
          // and the admin provisioned this account, so they're
          // covered by definition. If the community's subscription
          // lapses, the admin loses access to provision new
          // residents; existing residents continue until the admin
          // archives them.
          entitlementOk: true,
          rtdbPath: `brain_health/accounts/${residentAccount.id}`,
          record: residentAccount,
          raw: { residentAccount },
        };
      }
      if (type === CONTEXT_TYPES.INDIVIDUAL_USER && userRecord) {
        return {
          loading: false,
          type: CONTEXT_TYPES.INDIVIDUAL_USER,
          id: userRecord.id,
          displayName: userRecord.display_name || null,
          entitlementOk: individualHasEntitlement,
          rtdbPath: `brain_health/users/${userRecord.id}`,
          record: userRecord,
          raw: { userRecord },
        };
      }
    }

    return {
      loading: false,
      type: CONTEXT_TYPES.NONE,
      id: null,
      displayName: null,
      entitlementOk: false,
      rtdbPath: null,
      record: null,
      raw: null,
    };
  }, [
    residentAccount,
    residentLoading,
    device,
    deviceLoading,
    deviceHasEntitlement,
    userRecord,
    individualLoading,
    individualHasEntitlement,
  ]);

  return (
    <CurrentContextContext.Provider value={value}>
      {children}
    </CurrentContextContext.Provider>
  );
}

export function useCurrentContext() {
  const ctx = useContext(CurrentContextContext);
  if (!ctx) {
    throw new Error(
      'useCurrentContext must be used inside <CurrentContextProvider>'
    );
  }
  return ctx;
}
